import { GameEntity } from '@okletsplay/core';
import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { cancelationSubscribe } from 'app/services/api/utils';
import { entityStoreStatus } from 'app/utils/store/entity-store-status';
import { GamesState } from 'modules/games/store/games/interfaces';
import { GameStoreStatus } from 'modules/games/store/games/enums';
import { CreateGameRequest, UpdateGameRequest } from 'modules/games/store/games/interfaces';

import { entityAdapter } from 'modules/games/store/games/adapter';

export const getGamesList = createAsyncThunk(
  `${EntityStoreName.Games}/getList`,
  (_, { signal }): Promise<GameEntity[]> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<GameEntity[], GameEntity[]>({
      params: {
        method: 'GET',
        resource: ApiResource.Games,
        cancelToken,
      },
    });
  }
);

export const createGame = createAsyncThunk(
  `${EntityStoreName.Games}/addGame`,
  (data: CreateGameRequest): Promise<GameEntity> => {
    return apiRequestCall<GameEntity, GameEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Games,
        data,
      },
      successRequestMessage: `${data?.title} has been successfully created!`,
    });
  }
);

export const updateGame = createAsyncThunk(
  `${EntityStoreName.Games}/updateGame`,
  ({ identifier, data }: UpdateGameRequest): Promise<GameEntity> => {
    return apiRequestCall<GameEntity, GameEntity>({
      params: {
        method: 'PUT',
        resource: ApiResource.Games,
        urlPrefix: identifier,
        data,
      },
      successRequestMessage: `${data?.title} has been successfully updated!`,
    });
  }
);

export const clearAllReducer = (state: GamesState): void => {
  entityAdapter.removeAll(state);
};

export const registerReducers = (builder: ActionReducerMapBuilder<GamesState>): void => {
  builder
    .addCase(getGamesList.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, GameStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(getGamesList.fulfilled, (state, action) => {
      entityAdapter.setAll(state, action.payload);
      entityStoreStatus.removeRequestId(state, GameStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(getGamesList.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(state, GameStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(createGame.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, GameStoreStatus.Creating, action.meta.requestId);
    })
    .addCase(createGame.fulfilled, (state, action) => {
      entityAdapter.addOne(state, action.payload);
      entityStoreStatus.removeRequestId(state, GameStoreStatus.Creating, action.meta.requestId);
    })
    .addCase(createGame.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(state, GameStoreStatus.Creating, action.meta.requestId);
    })
    .addCase(updateGame.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, GameStoreStatus.Updating, action.meta.requestId);
    })
    .addCase(updateGame.fulfilled, (state, action) => {
      entityAdapter.updateOne(state, { id: action.payload.id, changes: action.payload });
      entityStoreStatus.removeRequestId(state, GameStoreStatus.Creating, action.meta.requestId);
    })
    .addCase(updateGame.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(state, GameStoreStatus.Creating, action.meta.requestId);
    });
};
